import { useEffect, useId, useRef, useState } from 'react'
import {
  HUBSPOT_PORTAL_ID,
  HUBSPOT_SCRIPT_SRC,
  formIdForRoute,
} from '@/components/marketing/hubspot-forms'
import { cn } from '@/lib/cn'

declare global {
  interface Window {
    hbspt?: {
      forms: {
        create: (opts: Record<string, unknown>) => void
      }
    }
  }
}


/**
 * One HubSpot form component, replacing the copy-pasted embed that ran on all
 * 61 captured pages.
 *
 * The capture loaded the HubSpot script and called `forms.create` from inline
 * script in every page head, whether or not that page had a form. Here the
 * script loads once, on demand, and the form is created into a container this
 * component owns and cleans up.
 */
export function HubSpotForm({
  formId,
  route,
  className,
}: {
  /** Explicit form id; otherwise resolved from `route`. */
  formId?: string
  route?: string
  className?: string
}) {
  const resolved = formId ?? formIdForRoute(route ?? '')
  const ref = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)
  // `forms.create` resolves `target` as a CSS selector, not an element, so the
  // container needs a stable id of its own. useId's colons are not valid in a
  // selector unescaped, hence the swap.
  const containerId = `hs-form-${useId().replace(/:/g, '')}`

  useEffect(() => {
    const target = ref.current
    if (!target) return
    let cancelled = false

    const create = () => {
      if (cancelled || !window.hbspt || !target) return
      target.innerHTML = ''
      window.hbspt.forms.create({
        region: 'na1',
        portalId: HUBSPOT_PORTAL_ID,
        formId: resolved,
        target: `#${containerId}`,
      })
    }

    if (window.hbspt) {
      create()
    } else {
      let script = document.querySelector<HTMLScriptElement>(`script[src="${HUBSPOT_SCRIPT_SRC}"]`)
      if (!script) {
        script = document.createElement('script')
        script.src = HUBSPOT_SCRIPT_SRC
        script.async = true
        document.head.appendChild(script)
      }
      script.addEventListener('load', create)
      script.addEventListener('error', () => !cancelled && setFailed(true))
    }

    return () => {
      cancelled = true
      if (target) target.innerHTML = ''
    }
  }, [resolved, containerId])

  if (failed) {
    return (
      <div className={cn('rounded-2xl border border-solid border-neutral-200 p-8', className)}>
        <p className="m-0">
          The form could not load.{' '}
          <a href="mailto:hello@singulate.com" className="text-blue underline">
            Email us instead
          </a>
          .
        </p>
      </div>
    )
  }

  return <div id={containerId} ref={ref} className={cn('min-h-[420px] w-full', className)} />
}
